import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'common/toast';

import { PageContent, PageHeader } from 'common/styles/page';
import Tabs from 'common/components/Tabs';
import { organizationApi } from '../api/organizationApi';
import OrganizationEmployeeRecordsList from './OrganizationEmployeeRecordsList';
import OrganizationRecordsList from './OrganizationRecordsList';
import OrganizationCategories from './OrganizationCategories';
import OrganizationPropertiesForm from './OrganizationProperties';
import OrganizationRemindersForm, { validateCronExpression } from './OrganizationReminders';
import { StyledCancelButton, StyledSaveButton } from 'common/components/button/StyledButton';
import LoadingSpinner from 'common/components/LoadingSpinner';
import OrganizationSurveyStructure from './OrganizationSurveyStructure';

export type OrganizationCategory = {
  id: string;
  name: string;
  visibility: 'public' | 'private';
  input: string;
  [k: string]: any;
};


export type OrgFormValue = {
  name: string;
  time_zone: string;
  tier?: number;
  team_leader_dashboard: boolean;
  nulogic_benchmarks: boolean;
  pulse_surveys: boolean;
  team_leader_dashboard_audience: 'all_leaders' | 'organization_admin_only';

  // moved from SurveyStructure
  default_min_followup: number | null;
  default_max_followup: number | null;
  default_survey_question: number | null;
  default_outcome_question: number | null;
  default_reminder_schedule?: string;
  categories: OrganizationCategory[]; 
};

const OrganizationEdit = () => {
  const { id } = useParams<{ id: string }>();
  const organizationId = Number(id);

  const navigate = useNavigate();
  const { addToast } = useToasts();

  const [activeTab, setActiveTab] = useState('survey');
  const [formValue, setFormValue] = useState<OrgFormValue>({
    name: '',
    time_zone: 'US/Eastern',
    tier: undefined,
    team_leader_dashboard: false,
    nulogic_benchmarks: false,
    pulse_surveys: false,
    team_leader_dashboard_audience: 'all_leaders',
    default_min_followup: null,
    default_max_followup: null,
    default_survey_question: null,
    default_outcome_question: null,
    default_reminder_schedule: '0 9 * * 1,3,5',
    categories: [],
  });
  const [formErrors, setFormErrors] = useState<any>({});

  const { data: org, isLoading } = organizationApi.useApiAdminV1OrganizationRetrieveQuery(
    { id: organizationId },
    { skip: !organizationId }
  );
  const [updateOrganization] = organizationApi.useApiAdminV1OrganizationPartialUpdateMutation();

  useEffect(() => {
    if (org) {
      setFormValue({
        name: org.name ?? '',
        time_zone: org.time_zone ?? 'US/Eastern',
        tier: (org as any).tier,
        default_min_followup: (org as any).default_min_followup ?? null,
        default_max_followup: (org as any).default_max_followup ?? null,
        default_survey_question: (org as any).default_survey_question ?? null,
        default_outcome_question: (org as any).default_outcome_question ?? null,
        default_reminder_schedule: (org as any).default_reminder_schedule ?? '0 9 * * 1,3,5',
        categories: (org as any).categories ?? [],
        team_leader_dashboard:
          (org as any).organization_options?.find((option: any) => option.key === 'team_leader_dashboard')?.enabled ?? false,
        nulogic_benchmarks:
          (org as any).organization_options?.find((option: any) => option.key === 'nulogic_benchmarks')?.enabled ?? false,
        pulse_surveys:
          (org as any).organization_options?.find((option: any) => option.key === 'pulse_surveys')?.enabled ?? false,
        team_leader_dashboard_audience:
          (org as any).organization_options?.find((option: any) => option.key === 'team_leader_dashboard')?.config?.audience || 'all_leaders',
      });
    }
  }, [org]);

  const handleSave = async () => {
    if (!formValue.name) {
      setFormErrors({ name: 'Organization name is required' });
      return;
    }

    // Validate cron expression if provided
    if (formValue.default_reminder_schedule) {
      const cronValidation = validateCronExpression(formValue.default_reminder_schedule);
      if (!cronValidation.valid) {
        setFormErrors({ default_reminder_schedule: cronValidation.error });
        addToast('Please fix validation errors before saving', { appearance: 'error' });
        return;
      }
    }

    try {
      await updateOrganization({
        id: organizationId,
        patchedOrganization: {
          name: formValue.name,
          time_zone: formValue.time_zone,
          tier: formValue.tier,
          default_min_followup: formValue.default_min_followup,
          default_max_followup: formValue.default_max_followup,
          default_survey_question: formValue.default_survey_question,
          default_outcome_question: formValue.default_outcome_question,
          default_reminder_schedule: formValue.default_reminder_schedule,
          categories: formValue.categories,
          organization_options: [
            {
              key: 'team_leader_dashboard',
              name: 'Team Leader Dashboard',
              enabled: formValue.team_leader_dashboard,
              config: { audience: formValue.team_leader_dashboard_audience },
            },
            {
              key: 'nulogic_benchmarks',
              name: 'nuLogic Benchmarks',
              enabled: formValue.nulogic_benchmarks,
              config: {},
            },
            {
              key: 'pulse_surveys',
              name: 'Pulse Surveys',
              enabled: formValue.pulse_surveys,
              config: {},
            },
          ],
        } as any,
      }).unwrap();

      addToast('Organization updated successfully', { appearance: 'success' });
      navigate(-1);
    } catch (err) {
      console.error('Failed to update organization:', err);
      addToast('Failed to update organization', { appearance: 'error' });
    }
  };

  const tabConfig = [
    {
      name: 'survey',
      label: 'Survey',
      content: (
        <OrganizationSurveyStructure
          organization={formValue}
          onOrganizationChange={(updated) => setFormValue(updated)}
          organizationId={organizationId}
        />
      ),
    },
    {
      name: 'categories',
      label: 'Categories',
      content: <OrganizationCategories organizationId={organizationId} />,
    },
    {
      name: 'records',
      label: 'Records',
      content: (
        <>
          <OrganizationEmployeeRecordsList organizationId={organizationId} />
          <OrganizationRecordsList organizationId={organizationId} />
        </>
      ),
    },
    {
      name: 'reminders',
      label: 'Reminders',
      content: (
        <OrganizationRemindersForm
          formValue={formValue}
          formErrors={formErrors}
          onChange={(field, value) => {
            setFormValue(prev => ({ ...prev, [field]: value }));
            // Clear error when user starts typing
            if (formErrors[field]) {
              setFormErrors((prev: any) => ({ ...prev, [field]: undefined }));
            }
          }}
        />
      ),
    },
    {
      name: 'properties',
      label: 'Properties',
      content: (
        <OrganizationPropertiesForm
          formValue={formValue}
          formErrors={formErrors}
          onChange={(field, value) => setFormValue(prev => ({ ...prev, [field]: value }))}
        />
      ),
    },
  ];

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageContent>
      <PageHeader
        title={formValue.name || 'Edit Organization'}
        divider
        controls={
          <div style={{ display: 'flex', gap: '1rem' }}>
            <StyledCancelButton onClick={() => navigate(-1)} />
            <StyledSaveButton onClick={handleSave} />
          </div>
        }
      />
      <Tabs tabs={tabConfig} activeTab={activeTab} onTabChange={setActiveTab} />
    </PageContent>
  );
};

export default OrganizationEdit;
